import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const statusText: Record<string, string> = { PREPARING: "Preparación pendiente; si fue interrumpida, puede comprobarse su recuperación exclusiva", DISPATCHING: "Envío iniciado; resultado pendiente, requiere comprobación",
  DRY_RUN: "Simulación: sin correo real", ACCEPTED: "Aceptado por SMTP; no acredita lectura ni entrega final", UNCERTAIN: "Resultado incierto: comprobar proveedor antes de cualquier reenvío",
  FAILED_PREPARATION: "Preparación fallida; no se inició envío", CANCELLED: "Cancelado antes de enviar: cambió la aprobación o un contacto" };
const deliveryErrorText: Record<string, string> = {
  EMAIL_SENDING_DISABLED: "El envío real está deshabilitado en este entorno.",
  SMTP_NOT_CONFIGURED: "No se pudo iniciar el envío porque el servidor de correo no está configurado.",
  EAUTH: "El proveedor de correo rechazó la autenticación.",
  ECONNECTION: "No se pudo conectar con el proveedor de correo.",
  ECONNREFUSED: "El proveedor de correo rechazó la conexión.",
  ETIMEDOUT: "Se agotó el tiempo de espera; el resultado puede ser incierto.",
  ESOCKET: "La conexión con el proveedor se interrumpió; el resultado puede ser incierto.",
  EENVELOPE: "El proveedor rechazó uno o más destinatarios.",
  RECIPIENTS_NOT_ACCEPTED: "El proveedor no confirmó la aceptación de todos los destinatarios.",
  PROVIDER_NO_CONFIRMATION: "El proveedor no confirmó el resultado del envío.",
  PDF_PREPARATION_FAILED: "El PDF aprobado no pudo prepararse o verificarse; no se inició el envío.",
  APPROVAL_CHANGED: "La aprobación o un contacto cambió; no se inició el envío.",
  RECOVERY_APPROVAL_OR_CONTACT_CHANGED: "La aprobación o un contacto cambió; no se inició el envío.",
};
const deliveryNoticeText: Record<string, string> = {
  PREPARING: "Preparación registrada; consulta el historial para ver su estado.",
  DISPATCHING: "Envío iniciado; el resultado sigue pendiente en el historial.",
  DRY_RUN: "Simulación registrada: no se envió ningún correo.",
  ACCEPTED: "SMTP aceptó el mensaje; la entrega final no está confirmada.",
  UNCERTAIN: "Resultado incierto; comprueba el proveedor antes de cualquier reenvío.",
  FAILED_PREPARATION: "No se inició el envío; consulta el motivo en el historial.",
  CANCELLED: "Operación cancelada antes del envío; consulta el historial.",
};
const scopeText = { contract: "Contrato", site: "Sede", installation: "Instalación", customer: "Cliente" };
type DeliveryMode = { mode: "DRY_RUN" | "LIVE"; dailyReportSendLiveEnabled: boolean; sendEmailsEnabled: boolean; smtpConfigured: boolean };
type DeliveryIntent = "SEND" | "RESEND";
type ReviewedOperation = { intent: DeliveryIntent; resendOf?: string; recipients: Array<{ assignmentId: number; email: string }>; mode: DeliveryMode };
const sameDeliveryMode = (a: DeliveryMode, b: DeliveryMode) => a.mode === b.mode
  && a.dailyReportSendLiveEnabled === b.dailyReportSendLiveEnabled
  && a.sendEmailsEnabled === b.sendEmailsEnabled && a.smtpConfigured === b.smtpConfigured;

export function ReportDeliveryPanel({ id, version, sourceHash, approved }: { id: number; version: number; sourceHash: string; approved: boolean }) {
  const mode = trpc.reportDelivery.mode.useQuery(undefined, { staleTime: 0, refetchOnWindowFocus: true });
  const options = trpc.reportDelivery.options.useQuery({ id });
  const history = trpc.reportDelivery.history.useQuery({ id });
  const [selected, setSelected] = useState<number[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [reviewedMode, setReviewedMode] = useState<DeliveryMode | null>(null);
  const [reviewedOperation, setReviewedOperation] = useState<ReviewedOperation | null>(null);
  const [checkingMode, setCheckingMode] = useState(false);
  // Keep this key on network errors: a retry reads the recorded result, never resends.
  const [key, setKey] = useState(() => crypto.randomUUID());
  const recipients = (options.data ?? []).filter(c => selected.includes(c.assignmentId));
  const uniqueEmails = Array.from(new Set(recipients.map(c => c.email)));
  const send = trpc.reportDelivery.send.useMutation({
    onSuccess: result => {
      setConfirming(false); setReviewedOperation(null);
      if (["ACCEPTED", "DRY_RUN", "UNCERTAIN"].includes(result.status)) setKey(crypto.randomUUID());
      void history.refetch(); toast.info(deliveryNoticeText[result.status] ?? "Operación registrada; consulta el historial para ver su estado.");
    },
    onError: error => { void history.refetch(); toast.error(error.message); },
  });
  const [recoveryId,setRecoveryId]=useState<string|null>(null);
  const recover=trpc.reportDelivery.recover.useMutation({onSuccess:result=>{setRecoveryId(null);void history.refetch();toast.info(deliveryNoticeText[result.status] ?? "Recuperación comprobada; consulta el historial para ver el estado.");},onError:error=>{void history.refetch();toast.error(error.message);}});
  const versionHistory = (history.data ?? []).filter(row => row.version === version);
  const active = versionHistory.some(row => ["PREPARING", "DISPATCHING", "UNCERTAIN"].includes(row.status));
  const lastTerminal = versionHistory.find(row => ["DRY_RUN", "ACCEPTED"].includes(row.status));
  const failedCurrent = (history.data ?? []).some(row => row.id === key && ["FAILED_PREPARATION", "CANCELLED"].includes(row.status));
  const liveRequirementsMissing = mode.data?.mode === "LIVE" && (!mode.data.sendEmailsEnabled || !mode.data.smtpConfigured);
  const blocked = !approved || active || send.isPending || checkingMode || mode.isLoading || mode.isFetching || mode.isError || !mode.data || liveRequirementsMissing
    || options.isLoading || options.isError || history.isLoading || history.isError;
  const refreshAndValidateMode = async (expected: DeliveryMode | null, compareWithReviewed: boolean): Promise<DeliveryMode | null> => {
    const latest = await mode.refetch();
    if (!latest.data || latest.isError) {
      toast.error("No se pudo verificar el modo actual; no se inició la operación.");
      return null;
    }
    setReviewedMode(latest.data);
    if (latest.data.mode === "LIVE" && (!latest.data.sendEmailsEnabled || !latest.data.smtpConfigured)) {
      toast.error("El modo LIVE está bloqueado por los requisitos actuales; no se inició la operación.");
      return null;
    }
    if (compareWithReviewed && (!expected || !sameDeliveryMode(latest.data, expected))) {
      toast.info("El modo o sus requisitos cambiaron. Revisa de nuevo antes de confirmar.");
      return null;
    }
    return latest.data;
  };
  const readCurrentSelection = async (currentMode: DeliveryMode) => {
    const [latestHistory, latestOptions] = await Promise.all([history.refetch(), options.refetch()]);
    if (latestHistory.isError || !Array.isArray(latestHistory.data) || latestOptions.isError || !Array.isArray(latestOptions.data)) {
      toast.error("No se pudieron actualizar historial o destinatarios. No se inició la operación.");
      return null;
    }
    const rows = latestHistory.data.filter(row => row.version === version);
    if (rows.some(row => ["PREPARING", "DISPATCHING", "UNCERTAIN"].includes(row.status))) {
      toast.info("Hay una operación pendiente o incierta. Comprueba su estado antes de iniciar otra.");
      return null;
    }
    const terminal = rows.find(row => ["DRY_RUN", "ACCEPTED"].includes(row.status));
    const latestRecipients = latestOptions.data.filter(contact => selected.includes(contact.assignmentId));
    if (!selected.length || latestRecipients.length !== selected.length) {
      toast.info("Un destinatario seleccionado ya no está disponible. Revisa la selección actualizada.");
      return null;
    }
    const action: ReviewedOperation = {
      intent: terminal ? "RESEND" : "SEND",
      ...(terminal ? { resendOf: terminal.id } : {}),
      recipients: latestRecipients.map(contact => ({ assignmentId: contact.assignmentId, email: contact.email }))
        .sort((a, b) => a.assignmentId - b.assignmentId),
      mode: currentMode,
    };
    return { action, intent: action.intent, resendOf: action.resendOf };
  };
  const reviewRecipients = async () => {
    setCheckingMode(true);
    try {
      const currentMode = await refreshAndValidateMode(null, false);
      if (!currentMode) {
        setConfirming(false);
        return;
      }
      const current = await readCurrentSelection(currentMode);
      if (!current) { setConfirming(false); return; }
      setReviewedOperation({ ...current.action, mode: currentMode });
      setConfirming(true);
    } finally { setCheckingMode(false); }
  };
  const confirmSend = async () => {
    setCheckingMode(true);
    try {
      if (!reviewedOperation) {
        toast.info("Revisa destinatarios antes de confirmar.");
        setConfirming(false);
        return;
      }
      const currentMode = await refreshAndValidateMode(reviewedOperation.mode, true);
      if (!currentMode) {
        setConfirming(false);
        return;
      }
      const current = await readCurrentSelection(currentMode);
      if (!current) { setConfirming(false); return; }
      if (current.intent !== reviewedOperation.intent || current.resendOf !== reviewedOperation.resendOf) {
        toast.info("El historial de envíos cambió. Revisa de nuevo antes de confirmar.");
        setConfirming(false);
        return;
      }
      if (JSON.stringify(current.action.recipients) !== JSON.stringify(reviewedOperation.recipients)) {
        toast.info("Un destinatario cambió desde la revisión. Comprueba la selección actualizada.");
        setConfirming(false);
        return;
      }
      send.mutate({ id, expectedVersion: version, expectedSourceHash: sourceHash,
        idempotencyKey: key, confirmed: true, intent: reviewedOperation.intent,
        ...(reviewedOperation.resendOf ? { resendOf: reviewedOperation.resendOf } : {}), recipients: reviewedOperation.recipients });
    } finally { setCheckingMode(false); }
  };
  const reviewRecovery = async (deliveryId: string) => {
    setCheckingMode(true);
    try {
      setRecoveryId(null); setConfirming(false); setReviewedOperation(null);
      if (await refreshAndValidateMode(null, false)) setRecoveryId(deliveryId);
    } finally { setCheckingMode(false); }
  };
  const confirmRecovery = async (deliveryId: string) => {
    setCheckingMode(true);
    try {
      if (!await refreshAndValidateMode(reviewedMode, true)) {
        setRecoveryId(null);
        return;
      }
      recover.mutate({ id, deliveryId, confirmed: true });
    } finally { setCheckingMode(false); }
  };
  return <section className="space-y-3 rounded-lg border bg-card p-4" aria-label="Enviar parte al cliente">
    <h2 className="text-xl font-semibold">{lastTerminal ? "Enviar o reenviar al cliente" : "Enviar al cliente"} · versión {version}</h2>
    <p className="text-sm">Un reenvío explícito crea un NUEVO correo y una operación de historial independiente; conserva los envíos anteriores. La aceptación SMTP no acredita lectura ni entrega final; una edición posterior exige nueva aprobación.</p>
    <div className="space-y-1 rounded border p-3 text-sm" aria-live="polite">
      <p className="font-medium">Preflight del modo de entrega</p>
      {(mode.isLoading || mode.isFetching || checkingMode) && <p role="status">Comprobando el modo actual; la confirmación está bloqueada.</p>}
      {mode.isError && <p role="alert">No se pudo comprobar el modo actual. Recarga antes de confirmar.</p>}
      {!mode.isFetching && !checkingMode && mode.data?.mode === "DRY_RUN" && <p role="status">Modo simulación (DRY_RUN): confirmar registrará una simulación y no enviará correo real. Flag live: {mode.data.dailyReportSendLiveEnabled ? "habilitado" : "deshabilitado"}. Requisitos LIVE: email {mode.data.sendEmailsEnabled ? "habilitado" : "deshabilitado"}; SMTP {mode.data.smtpConfigured ? "configurado" : "incompleto"}.</p>}
      {!mode.isFetching && !checkingMode && mode.data?.mode === "LIVE" && liveRequirementsMissing && <p role="alert">Modo LIVE bloqueado: {mode.data.sendEmailsEnabled ? "" : "el envío de email está deshabilitado"}{!mode.data.sendEmailsEnabled && !mode.data.smtpConfigured ? " y " : ""}{mode.data.smtpConfigured ? "" : "faltan requisitos SMTP"}. No se puede confirmar.</p>}
      {!mode.isFetching && !checkingMode && mode.data?.mode === "LIVE" && !liveRequirementsMissing && <p role="status">Modo LIVE habilitado por la configuración actual; SEND_EMAILS_ENABLED está habilitado y SMTP configurado. Confirmar intentará el envío, pero la aceptación del proveedor no acredita entrega final.</p>}
    </div>
    {!approved && <p role="status">Bloqueado: guarda los cambios y comprueba la vista previa del PDF actual.</p>}
    {active && <p role="status">Bloqueado: hay una operación pendiente o incierta. No se puede reenviar a ciegas.</p>}
    {(options.isError || history.isError) && <p role="alert">No se pudieron cargar contactos o historial. Recarga antes de enviar.</p>}
    <ul className="space-y-2">{(options.data ?? []).map(contact => <li key={contact.assignmentId}>
      <label className="flex min-w-0 items-start gap-2 text-sm"><input type="checkbox" className="shrink-0" checked={selected.includes(contact.assignmentId)} disabled={blocked}
        onChange={event => { setConfirming(false); setSelected(event.target.checked ? [...selected, contact.assignmentId] : selected.filter(id => id !== contact.assignmentId)); }} />
        <span className="min-w-0 break-words">{contact.name} · {contact.jobTitle || "Sin cargo registrado"} · {scopeText[contact.scope]} · {contact.email}</span>
      </label>
    </li>)}</ul>
    {!options.isLoading && !options.isError && options.data?.length === 0 && <p>Sin contactos canónicos con email válido. Completa los contactos del cliente antes de enviar.</p>}
    {failedCurrent && <Button variant="outline" disabled={send.isPending} onClick={() => { setKey(crypto.randomUUID()); setConfirming(false); }}>Reiniciar operación fallida</Button>}
    <Button disabled={blocked || recipients.length === 0 || failedCurrent} onClick={() => { void reviewRecipients(); }}>
      {lastTerminal ? "Revisar destinatarios para reenviar" : "Revisar contactos seleccionados"}
    </Button>
    {confirming && <div className="space-y-2 rounded border p-3" role="group" aria-label="Confirmar destinatarios">
      <p>{reviewedOperation?.intent === "RESEND"
        ? `Esto enviará un NUEVO correo con el PDF conservado de la versión ${version}; el correo anterior no se modifica. Destinatarios (${new Set(reviewedOperation.recipients.map(c => c.email)).size} emails distintos):`
        : `Enviar el PDF aprobado de la versión ${version} a ${uniqueEmails.length} emails distintos:`}</p>
      <ul>{(reviewedOperation?.intent === "RESEND" ? reviewedOperation.recipients.map(c => c.email) : uniqueEmails)
        .filter((address, index, all) => all.indexOf(address) === index).map(address => <li key={address}>{address}</li>)}</ul>
      <p className="text-sm">Se registra una operación nueva e independiente. Un resultado incierto requiere comprobación, sin reenvío automático.</p>
      <Button disabled={blocked || recipients.length === 0} onClick={() => { void confirmSend(); }}>
        {reviewedOperation?.intent === "RESEND" ? "Confirmar reenvío (nuevo correo)" : "Confirmar envío al cliente"}
      </Button>
    </div>}
    <h3 className="font-medium">Historial</h3>
    <ul className="space-y-3">{(history.data ?? []).map(row => <li key={row.id} className="space-y-1 rounded border p-2 text-sm">
      <p>Versión {row.version} · usuario {row.sentBy} · {new Date(row.requestedAt).toLocaleString("es-ES")}</p>
      <p>Operación {row.id}{row.result?.operation === "RESEND" && row.result?.resendOf ? ` · reenvío explícito de ${row.result.resendOf}` : ""}</p>
      <p>{statusText[row.status] ?? row.status}</p>
      {(row.result?.errorCode || row.result?.reason) && <p role="status">{deliveryErrorText[row.result.errorCode ?? row.result.reason] ?? "Resultado pendiente de reconciliación con el proveedor."}</p>}
      {row.status==="PREPARING" && !row.dispatchAt && <div>
        <Button variant="outline" disabled={checkingMode||recover.isPending||send.isPending} onClick={()=>{void reviewRecovery(row.id);}}>Comprobar recuperación de preparación</Button>
        {recoveryId===row.id && <div role="group" aria-label="Confirmar recuperación">
          <p>Se conservarán esta operación, versión y destinatarios. Solo se recupera si ningún proceso la posee y no se ha iniciado envío; un resultado incierto exige reconciliación.</p>
          <p>Modo verificado para esta recuperación: {reviewedMode?.mode === "DRY_RUN" ? "DRY_RUN, simulación sin correo real." : reviewedMode?.mode === "LIVE" ? "LIVE, con requisitos de email y SMTP presentes; la aceptación no prueba entrega final." : "sin verificar; vuelve a revisar."}</p>
          <Button disabled={checkingMode||recover.isPending||send.isPending||mode.isLoading||mode.isFetching||mode.isError||liveRequirementsMissing}
            onClick={()=>{void confirmRecovery(row.id);}}>Confirmar recuperación de la misma operación</Button>
        </div>}
      </div>}
      {row.dispatchAt && <p>Envío iniciado: {new Date(row.dispatchAt).toLocaleString("es-ES")}</p>}
      {row.result?.messageId && <p className="break-all">Referencia SMTP: {row.result.messageId}</p>}
      <p className="break-words">{(row.recipients as Array<{ email: string }>).map(r => r.email).join(", ")}</p>
      {row.pdfUrl && <a className="underline" href={row.pdfUrl} target="_blank" rel="noopener noreferrer">Ver PDF conservado de la versión {row.version}</a>}
      {row.pdfHash && <p className="break-all text-xs">SHA-256: {row.pdfHash}</p>}
    </li>)}</ul>
  </section>;
}
